import { Header, HeaderBreadcrumb } from 'common/ui/Header';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  getSourcingSharedCampaignTypeDefaultValue,
  getSourcingSharedCustomizeMessageFields,
  sourcingSharedCustomizeMessageEmailSchema,
  SourcingSharedCustomizeMessageEmailFormData,
  sourcingSharedCustomizeMessageSmsSchema,
  SourcingSharedCustomizeMessageSmsFormData,
  SourcingSharedSetupApplicationFormData
} from 'modules/sourcing-shared/forms';
import { type CampaignCreationSharedJobInformationFormData } from 'modules/campaign-creation-shared/forms';
import { Stack, SxProps, Theme } from '@mui/material';
import { Form, FormRef } from 'common/form';
import { Button } from 'system/atoms';
import { Replay } from '@mui/icons-material';
import { SourcingLoadingIndicator } from 'common/animations/SourcingLoadingIndicator';
import { getCampaignCreationTemplateVariables } from 'pages/CampaignCreation/utils/getCampaignCreationTemplateVariables';
import {
  CampaignType,
  Channels,
  CustomTemplatedMessageVariables
} from 'generated-graphql/types';
import { CampaignType as AppCampaignType } from 'services/campaign/types';
import { capitalizeFirstLetter } from 'utils/fomatting';
import { getSourcingSharedDefaultOutreachTemplatedWithEndingMessageController } from 'modules/sourcing-shared/controllers/getSourcingSharedDefaultOutreachTemplatedWithEndingMessageController';
import { validateMultipleTemplatedInputs } from 'core/utils/validators/validateTemplatedInputVariables';
import { useModalV2 } from 'common/modal/hooks/useModalV2';
import { ERROR_MESSAGES } from 'core/constants/errorMessages';
import { ToastType, useToast } from 'services/toast';

const getAvailableVariables = (campaignType: CampaignType) => {
  return getCampaignCreationTemplateVariables(
    campaignType as unknown as AppCampaignType
  ).map((variable) => ({
    label: capitalizeFirstLetter(variable.toLowerCase().replaceAll('_', ' ')),
    value: variable
  }));
};

const revertButtonStyles: SxProps<Theme> = {
  alignSelf: 'flex-start',
  p: '0 1px',
  mb: '20px'
};

type GetShouldShowRevertButtonProps = {
  formEditedOpeningMessage: string | null;
  editedOpeningMessage: string | null;
  defaultTemplatedMessageWithEndingMessage: string | null;
  campaignType: CampaignType;
  applicationFormData: SourcingSharedSetupApplicationFormData | null;
};

const getShouldShowRevertButton = ({
  formEditedOpeningMessage,
  editedOpeningMessage,
  defaultTemplatedMessageWithEndingMessage,
  campaignType,
  applicationFormData
}: GetShouldShowRevertButtonProps) => {
  if (!formEditedOpeningMessage || !defaultTemplatedMessageWithEndingMessage)
    return false;
  if (applicationFormData && applicationFormData?.campaignType !== campaignType)
    return false;
  const baselineMessage =
    editedOpeningMessage || defaultTemplatedMessageWithEndingMessage;
  return formEditedOpeningMessage !== baselineMessage;
};

const CURRENT_BREADCRUMB_TITLE = 'Opening message';

type SourcingSharedCustomizeMessageStepProps = {
  headerBreadcrumbs: HeaderBreadcrumb[];
  applicationFormData: SourcingSharedSetupApplicationFormData | null;
  candidateBatchAtsJobId: boolean;
  candidateBatchAtsJobPostingUrl: string | undefined | null;
  editedOpeningMessage: string | null;
  defaultStartingTemplatedMessage: string | null;
  isDefaultStartingTemplatedMessageLoading: boolean;
  selectedRowLength: number;
  customizeMessageFormData:
    | SourcingSharedCustomizeMessageEmailFormData
    | SourcingSharedCustomizeMessageSmsFormData
    | null;
  jobInformationFormData: CampaignCreationSharedJobInformationFormData | null;
  onCustomizeMessageBack: () => void;
  onCustomizeMessageNext: (
    formData:
      | SourcingSharedCustomizeMessageEmailFormData
      | SourcingSharedCustomizeMessageSmsFormData
  ) => void;
  onCustomizeMessageInviteSubmit: (
    formData:
      | SourcingSharedCustomizeMessageEmailFormData
      | SourcingSharedCustomizeMessageSmsFormData
  ) => Promise<void>;
  channelType: Channels;
};

export const SourcingSharedCustomizeMessageStep: React.FC<
  SourcingSharedCustomizeMessageStepProps
> = ({
  headerBreadcrumbs,
  applicationFormData,
  candidateBatchAtsJobId,
  candidateBatchAtsJobPostingUrl,
  editedOpeningMessage,
  defaultStartingTemplatedMessage,
  isDefaultStartingTemplatedMessageLoading,
  selectedRowLength,
  customizeMessageFormData,
  jobInformationFormData,
  onCustomizeMessageBack,
  onCustomizeMessageNext,
  onCustomizeMessageInviteSubmit,
  channelType
}) => {
  const formRef =
    useRef<
      FormRef<
        | SourcingSharedCustomizeMessageEmailFormData
        | SourcingSharedCustomizeMessageSmsFormData
      >
    >(null);
  const { openModal } = useModalV2();
  const { showToast } = useToast();

  const campaignType = getSourcingSharedCampaignTypeDefaultValue({
    applicationFormData,
    hasAtsJobId: !!candidateBatchAtsJobId,
    hasAtsJobApplicationUrl: !!candidateBatchAtsJobPostingUrl
  });

  const availableVariables = getAvailableVariables(campaignType);

  const defaultTemplatedMessageWithEndingMessage =
    getSourcingSharedDefaultOutreachTemplatedWithEndingMessageController({
      campaignType,
      startingMessage: defaultStartingTemplatedMessage || '',
      channelType
    });

  const [formEditedOpeningMessage, setFormEditedOpeningMessage] =
    useState<string>('');

  const [isSubmitLoading, setIsSubmitLoading] = useState(false);

  // Initialize form with default message based on campaignType when first arriving at this step
  useEffect(() => {
    if (
      !formRef.current ||
      !applicationFormData ||
      !defaultTemplatedMessageWithEndingMessage
    )
      return;

    // This is for when we go forward then back to this step
    if (customizeMessageFormData) {
      formRef.current.setValue('agent', customizeMessageFormData.agent);
      if ('customTemplatedSubjectLine' in customizeMessageFormData) {
        (
          formRef.current as FormRef<SourcingSharedCustomizeMessageEmailFormData>
        ).setValue(
          'customTemplatedSubjectLine',
          customizeMessageFormData.customTemplatedSubjectLine
        );
      }
      formRef.current.setValue(
        'customTemplatedMessage',
        customizeMessageFormData.customTemplatedMessage
      );
      setFormEditedOpeningMessage(
        customizeMessageFormData.customTemplatedMessage
      );

      // This is for when we change the campaign type in previous step
    } else if (applicationFormData.campaignType !== campaignType) {
      formRef.current.setValue(
        'customTemplatedMessage',
        defaultTemplatedMessageWithEndingMessage
      );
      setFormEditedOpeningMessage(defaultTemplatedMessageWithEndingMessage);
    } else {
      // First time arriving at this step - initialize with default message
      const defaultMessage =
        editedOpeningMessage || defaultTemplatedMessageWithEndingMessage;

      formRef.current.setValue('customTemplatedMessage', defaultMessage);
      setFormEditedOpeningMessage(defaultMessage);
    }
  }, [
    customizeMessageFormData,
    jobInformationFormData,
    applicationFormData,
    defaultTemplatedMessageWithEndingMessage,
    editedOpeningMessage,
    campaignType
  ]);

  useEffect(() => {
    if (!formRef.current) return;

    const { unsubscribe } = formRef.current.watch(
      (
        value:
          | SourcingSharedCustomizeMessageEmailFormData
          | SourcingSharedCustomizeMessageSmsFormData
      ) => {
        if (
          value.customTemplatedMessage !== undefined &&
          value.customTemplatedMessage !== formEditedOpeningMessage
        ) {
          setFormEditedOpeningMessage(value.customTemplatedMessage);
        }
      }
    );

    return () => unsubscribe();
  }, []);

  const handleRevertToOriginalDefaultMessage = () => {
    if (!formRef.current) return;

    const messageToRevertTo =
      editedOpeningMessage || defaultTemplatedMessageWithEndingMessage || '';

    formRef.current.setValue('customTemplatedMessage', messageToRevertTo);
  };

  const shouldShowRevertButton = getShouldShowRevertButton({
    formEditedOpeningMessage,
    defaultTemplatedMessageWithEndingMessage,
    campaignType,
    applicationFormData,
    editedOpeningMessage
  });

  const handleValidation = useCallback(
    (
      data:
        | SourcingSharedCustomizeMessageEmailFormData
        | SourcingSharedCustomizeMessageSmsFormData
    ): {
      isValid: boolean;
      isDefaultMessage?: boolean;
    } => {
      const isValid = validateMultipleTemplatedInputs({
        variables: availableVariables.map(
          (item) => item.value as CustomTemplatedMessageVariables
        ),
        values: [
          'customTemplatedSubjectLine' in data
            ? data.customTemplatedSubjectLine
            : undefined,
          data.customTemplatedMessage
        ]
      });

      if (!isValid) {
        openModal({
          title: 'Invalid variables',
          subtitle: ERROR_MESSAGES.templatedInputVariableMustBeValid,
          primaryButtonText: 'OK',
          primaryButtonProps: { variant: 'primary' }
        });
        return { isValid: false };
      }

      const defaultMessage =
        editedOpeningMessage || defaultTemplatedMessageWithEndingMessage;

      return {
        isValid: true,
        isDefaultMessage: data.customTemplatedMessage === defaultMessage
      };
    },
    [
      availableVariables,
      editedOpeningMessage,
      defaultTemplatedMessageWithEndingMessage,
      openModal,
      ERROR_MESSAGES
    ]
  );

  const handleInviteSubmit = useCallback(
    async (
      data:
        | SourcingSharedCustomizeMessageEmailFormData
        | SourcingSharedCustomizeMessageSmsFormData
    ) => {
      try {
        setIsSubmitLoading(true);
        await onCustomizeMessageInviteSubmit(data);
      } catch (error) {
        showToast({
          message: 'Failed to invite candidates. Please try again.',
          type: ToastType.Error
        });
      } finally {
        setIsSubmitLoading(false);
      }
    },
    [onCustomizeMessageInviteSubmit, showToast, setIsSubmitLoading]
  );

  const handleReviewInvitations = useCallback(
    async (
      data:
        | SourcingSharedCustomizeMessageEmailFormData
        | SourcingSharedCustomizeMessageSmsFormData
    ) => {
      const { isValid } = handleValidation(data);

      if (!isValid) return;

      onCustomizeMessageNext(data);
    },
    [handleValidation, onCustomizeMessageNext]
  );

  const handleFormSubmit = useCallback(
    async (
      data:
        | SourcingSharedCustomizeMessageEmailFormData
        | SourcingSharedCustomizeMessageSmsFormData
    ) => {
      const { isValid } = handleValidation(data);
      if (!isValid) return;
      await handleInviteSubmit(data);
    },
    [handleValidation, handleInviteSubmit]
  );

  if (isDefaultStartingTemplatedMessageLoading) {
    return <SourcingLoadingIndicator title="Building your invitations" />;
  }

  const formOptions = {
    ref: formRef,
    fields: getSourcingSharedCustomizeMessageFields({
      availableVariables,
      defaultCustomTemplatedMessage:
        editedOpeningMessage || defaultTemplatedMessageWithEndingMessage || '',
      channelType
    }),
    submitButtonProps: {
      label: `Send ${selectedRowLength} invitation${
        selectedRowLength > 1 ? 's' : ''
      } now`,
      isLoading: isSubmitLoading
    },
    onSubmit: handleFormSubmit,
    onSecondaryButtonClick: handleReviewInvitations,
    secondaryButtonProps: {
      label: 'Review invitations'
    },
    children: shouldShowRevertButton && (
      <Button
        sx={revertButtonStyles}
        variant="link"
        startIcon={<Replay />}
        onClick={handleRevertToOriginalDefaultMessage}
      >
        Revert to original
      </Button>
    )
  };

  const schema =
    channelType === Channels.Sms
      ? sourcingSharedCustomizeMessageSmsSchema
      : sourcingSharedCustomizeMessageEmailSchema;

  return (
    <>
      <Header
        title={CURRENT_BREADCRUMB_TITLE}
        breadcrumbs={headerBreadcrumbs}
        currentBreadcrumb={CURRENT_BREADCRUMB_TITLE}
        onBack={onCustomizeMessageBack}
      />
      <Stack direction="column" spacing="40px">
        <Stack direction="column" spacing="40px" maxWidth="700px">
          <Form {...formOptions} schema={schema} />
        </Stack>
      </Stack>
    </>
  );
};
